/** @file Payment capture section */

import React, { useState } from 'react';
import { Box, Divider, SxProps, Typography } from '@mui/material';
import Accordion from './Accordion';
import { testId as parentTestId } from '../util';
import { SettlementTypeValue } from '../../../store/modules/merchant/types';

export const testId = `${parentTestId}-PAYMENT_CAPTURE`;

const styles: { [id: string]: SxProps } = {
  accContainer: {
    border: `1px solid`,
    borderColor: 'border.main',
    borderRadius: '8px',
    marginTop: 2,
    width: { xs: '100%', sm: '448px' },
    '& > .MuiPaper-root': {
      borderRadius: '8px !important',
      boxShadow: 'none',
    },
  },
  captureAcc: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
};

type Props = {
  merchantName: string;
  settlementType?: SettlementTypeValue;
  avsDecline?: boolean;
  onChangeSettlementType: (arg0: SettlementTypeValue | undefined) => any;
  onChangeAvsDecline: (arg0: boolean) => any;
  readOnly?: boolean;
  sx?: SxProps;
};

/**
 * Payment capture section of Payments and Processing
 * @param {Props} props Component Properties
 * @returns {JSX.Element}
 */
const PaymentCapture = ({
  merchantName,
  settlementType,
  avsDecline,
  onChangeSettlementType,
  onChangeAvsDecline,
  readOnly,
  sx = {},
}: Props) => {
  const [isManualOpen, setIsManualOpen] = useState(!settlementType);
  const [isImmediateOpen, setIsImmediateOpen] = useState(!settlementType);
  const [isAvsDeclineOpen, setIsAvsDeclineOpen] = useState(avsDecline === undefined);

  const showAvsDecline = true;

  return (
    <Box sx={sx} component="section" data-testid={testId}>
      <Typography variant="large" fontWeight="bold" sx={{ mb: 1 }}>
        Payment Capture
      </Typography>
      <Typography variant="small" color="text.secondary">
        {`Please select one of the following options for how ${merchantName} secures payments.`}
      </Typography>
      <Box sx={styles.accContainer}>
        {/* Manual Capture */}
        <Accordion
          disabled={readOnly}
          data-testid={`${testId}-MANUAL`}
          isExpanded={isManualOpen}
          isSwitchChecked={settlementType === 'AuthCapture'}
          onExpandToggle={() => setIsManualOpen((val) => !val)}
          onSwitchToggle={() =>
            onChangeSettlementType(settlementType === 'AuthCapture' ? undefined : 'AuthCapture')
          }
          sx={styles.captureAcc}
          title="Manual Capture"
        >
          Skipify will send an authorization to your gateway in the amount of the purchase price.
          You can then either use the Skipify Capture API or your own integration to your gateway to
          capture funds when appropriate.{' '}
          <b>This is most commonly used for merchants who ship products.</b>
        </Accordion>
        <Divider />
        {/* Immediate Capture */}
        <Accordion
          disabled={readOnly}
          data-testid={`${testId}-IMMEDIATE`}
          isExpanded={isImmediateOpen}
          isSwitchChecked={settlementType === 'Sale'}
          onExpandToggle={() => setIsImmediateOpen((val) => !val)}
          onSwitchToggle={() =>
            onChangeSettlementType(settlementType === 'Sale' ? undefined : 'Sale')
          }
          sx={styles.captureAcc}
          title="Immediate Capture"
        >
          Skipify will send both an authorization and capture request to your gateway, resulting in
          an immediate funds capture.{' '}
          <b>
            This is most commonly used for merchants who sell products or services that do not
            require shipping.
          </b>
        </Accordion>
      </Box>
      {showAvsDecline && (
        <Box sx={styles.accContainer}>
          <Accordion
            disabled={readOnly}
            data-testid={`${testId}-AVSDECLINE`}
            isExpanded={isAvsDeclineOpen}
            isSwitchChecked={!!avsDecline}
            onExpandToggle={() => setIsAvsDeclineOpen((val) => !val)}
            onSwitchToggle={() => onChangeAvsDecline(!avsDecline)}
            sx={styles.captureAcc}
            title="AVS Decline"
          >
            Address verification (AVS) is a fraud detection feature. AVS will fail if the address
            provided for the transaction does not match the address the card issuer has on file.{' '}
            <b>
              If a transaction is completed successfully, but the AVS check fails, Skipify will
              reverse the transaction on your behalf.
            </b>
          </Accordion>
        </Box>
      )}
    </Box>
  );
};

export default PaymentCapture;
